/**
 * CXP-local categorization status. Isolates the CRL surface from the API enum
 * `RequestSubmissionFileStatus` (translated in the bridge mapper, the isolation
 * seam) so `models/` and selectors never import the API layer. The API's
 * `AUTOMATICALLY_CATEGORIZED` collapses into `CATEGORIZED` here — the UI treats
 * auto- and manually-categorized identically.
 */
export enum CrlCategorizationStatus {
  UPLOADING = 'UPLOADING',
  CATEGORIZING = 'CATEGORIZING',
  CATEGORIZED = 'CATEGORIZED',
  UNCATEGORIZED = 'UNCATEGORIZED',
}

/**
 * A single bulk-uploaded CRL file as seen through the categorization
 * subscription. `status` is the live `client_request_submission_file.status`
 * column driven by the backend categorization process; `name` is stripped of the
 * `_<epoch>` upload suffix for display; `isLinked` is true once the file has been
 * categorized into at least one request item.
 */
export interface CrlCategorizationFile {
  id: number;
  fileId: number;
  name: string;
  size: number | null;
  dmsId: string | null;
  status: CrlCategorizationStatus;
  isLinked: boolean;
}

/**
 * View model for the uncategorized-files card below the bulk dropzone. Counts are
 * whole-request live totals: `categorizedCount` covers settled (auto/manually)
 * categorized files, `uncategorizedCount` the settled uncategorized ones. The
 * card is visible only while at least one uncategorized file exists — a file
 * still categorizing is not counted, so a fresh drop keeps the card hidden until
 * the background process resolves.
 */
export interface CrlUncategorizedCardVm {
  visible: boolean;
  categorizedCount: number;
  uncategorizedCount: number;
  files: CrlCategorizationFile[];
}

/**
 * Which items of the open request carry comments, as seen through the comments
 * subscription. `withComments` drives the row's speech-bubble add-on;
 * `withUnviewed` — always a subset — drives its teal dot. Ids only: comment
 * bodies are never read here (the thread itself is fetched per item when the
 * overlay's Comments tab opens).
 */
export interface CrlItemCommentIds {
  withComments: number[];
  withUnviewed: number[];
  /**
   * Each item's newest message id. An open thread watches its own entry: the id
   * changing is the only signal that a comment landed, since the id LISTS above
   * stay identical when an item that already had comments gains another.
   */
  latestMessageIdByItem: Record<number, number>;
}
